/* eslint-disable max-lines -- the rule was disabled before a comment became required */
'use client'

import { type AxiosError } from 'axios'
import { type FormModel } from 'formtastisch'
import { useSearchParams } from 'next/navigation'
import {
  type FC,
  type MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import {
  type Authentication400,
  CustomerTypeV1,
  SalutationV1,
} from '@redteclab/api/clients/bully'

import { Notification } from 'base-ui'

import { useExperimentsContext } from '../../../experiments/components/ExperimentsContext'
import { useModifyRegistrationExperiment } from '../../../experiments/modify-registration/useModifyRegistrationExperiment'
import { FriendlyCaptchaChallenge } from '../../../friendly-captcha'
import { useGlobalConfigContext } from '../../../global-config'
import { TENANT, type TenantLanguage, tenantToVatPrefix } from '../../../tenant'
import { timeGetIsoDateString } from '../../../time'
import { useUserRegistrationMutation } from '../../api/swr/useUserRegistrationMutation'
import {
  ERR_MESSAGES_FIELD_MAP_REGISTRATION,
  TARGET_PAGE_CODE_PARAM,
  TARGET_PAGE_REDIRECT_PARAM,
} from '../../consts'
import { LoginRegistrationRegistrationFormStateProvider } from '../../context/LoginRegistrationRegistrationFormStateContext'
import { LoginRegistrationRegistrationStateContext } from '../../context/LoginRegistrationRegistrationStateContext'
import {
  getRegistrationApiInvalidParamsErrorKeys,
  getTranslationErrorMessagesForRegistration,
} from '../../registrationApiError'
import { resolveRegistrationFlowConfig } from '../../registrationFlowConfig'
import {
  registrationFormMakeRegistrationFormState,
  type RegistrationFormState,
} from '../../registrationFormMakeRegistrationFormState'
import {
  type RegistrationOrigin,
  resolveRegistrationOrigin,
} from '../../registrationOrigin'
import { LoginRegistrationBirthDaySelect } from '../LoginRegistrationBirthDaySelect/LoginRegistrationBirthDaySelect'
import { LoginRegistrationModifyRegistrationPasswordSection } from '../LoginRegistrationModifyRegistrationPasswordSection/LoginRegistrationModifyRegistrationPasswordSection'
import { LoginRegistrationSalutation } from '../LoginRegistrationSalutation/LoginRegistrationSalutation'
import { LoginRegistrationVatSelectionAndInput } from '../LoginRegistrationVatSelectionAndInput/LoginRegistrationVatSelectionAndInput'

import { LoginRegistrationNewsletterAcceptedCheckbox } from './LoginRegistrationNewsletterAcceptedCheckbox'
import { LoginRegistrationRegistrationFormButtons } from './LoginRegistrationRegistrationFormButtons'
import { LoginRegistrationRegistrationFormCompanyInput } from './LoginRegistrationRegistrationFormCompanyInput'
import { LoginRegistrationRegistrationFormEmailInput } from './LoginRegistrationRegistrationFormEmailInput'
import { LoginRegistrationRegistrationFormFirstNameInput } from './LoginRegistrationRegistrationFormFirstNameInput'
import { LoginRegistrationRegistrationFormLastNameInput } from './LoginRegistrationRegistrationFormLastNameInput'
import { LoginRegistrationRegistrationFormPasswordInput } from './LoginRegistrationRegistrationFormPasswordInput'
import {
  DEFAULT_COUNTRY_CODE_COM,
  LoginRegistrationRegistrationFormPhoneWithInfo,
} from './LoginRegistrationRegistrationFormPhoneWithInfo'
import { LoginRegistrationRegistrationFormTosAcceptedCheckbox } from './LoginRegistrationRegistrationFormTosAcceptedCheckbox'

interface RegistrationFormProps {
  afterLoggedIn: (afterLoggedInSideEffectsPromise: Promise<void>) => void
  onClose: MouseEventHandler
}

const getSalutationFromString = (input: string): SalutationV1 => {
  const lowerCaseInput = input.toLowerCase()
  for (const key in SalutationV1) {
    if (SalutationV1[key as keyof typeof SalutationV1] === lowerCaseInput) {
      return SalutationV1[key as keyof typeof SalutationV1]
    }
  }

  return SalutationV1.Unspecified
}

type HandleSubmitProps = {
  hasVatNumber: boolean
  isProfessionalRegistration: boolean
  preferredLanguage: TenantLanguage
  registrationFlowVersion: 'v3' | 'v4'
  registrationFormState: RegistrationFormState
  registrationOrigin: RegistrationOrigin
  tenant: TENANT
  userRegistrationSwr: ReturnType<typeof useUserRegistrationMutation>
}

const handleSubmit = async ({
  hasVatNumber,
  isProfessionalRegistration,
  preferredLanguage,
  registrationFlowVersion,
  registrationFormState,
  registrationOrigin,
  tenant,
  userRegistrationSwr,
}: HandleSubmitProps): Promise<void> => {
  if (userRegistrationSwr.isMutating) {
    return
  }
  const { model } = registrationFormState

  // missing phone should not prevent form submission
  // we clear the field if it contains only the default country code
  // as that indicates that the user has not entered a phone number
  if (model.phone === DEFAULT_COUNTRY_CODE_COM) {
    registrationFormState.setValue('', model, 'phone')
  }

  registrationFormState.validate()
  if (!registrationFormState.isValid()) {
    return
  }

  // eslint-disable-next-line unicorn/try-complexity -- don't reference, refactor if touched
  try {
    await userRegistrationSwr.trigger({
      ...(isProfessionalRegistration && {
        companyName: model.companyName,
      }),
      customerType: isProfessionalRegistration
        ? CustomerTypeV1.Professional
        : CustomerTypeV1.Customer,
      dateOfBirth: timeGetIsoDateString(
        model.birthDaySelectedDay,
        model.birthDaySelectedMonth,
        model.birthDaySelectedYear,
      ),
      email: model.email,
      fcSolution: model.fcSolution,
      firstName: model.firstName,
      lastName: model.lastName,
      newsletterAccepted: model.newsletterAccepted,
      password: model.password,
      registrationFlowVersion,
      registrationOrigin,
      salutation: getSalutationFromString(model.salutation),
      tosAccepted: model.tosAccepted,
      ...(isProfessionalRegistration &&
        hasVatNumber && {
          vatNumber: tenantToVatPrefix[tenant] + model.vatNumber,
        }),
      phone: model.phone,
      preferredLanguage,
    })
  } catch {
    // void - error is handled in component (mutation error)
  }
}

const handleValidationErrors = (
  intl: IntlShape,
  registrationApiErrorInvalidParamKeys: string[],
  model: FormModel,
): void => {
  if (registrationApiErrorInvalidParamKeys.length === 0) {
    return
  }

  const translatedErrors = getTranslationErrorMessagesForRegistration(intl)
  model.validator.resetErrors()
  for (const invalidParamKey of registrationApiErrorInvalidParamKeys) {
    const fieldName = ERR_MESSAGES_FIELD_MAP_REGISTRATION.get(invalidParamKey)
    const errorMessage =
      translatedErrors.get(invalidParamKey) ??
      intl.formatMessage({ id: 'general.error' })
    if (fieldName !== undefined) {
      model.validator.addError(fieldName, errorMessage)
    }
  }
}

export const LoginRegistrationRegistrationForm: FC<RegistrationFormProps> = ({
  afterLoggedIn,
  onClose,
  // eslint-disable-next-line max-lines-per-function, max-statements -- don't reference, refactor if touched
}) => {
  const searchParams = useSearchParams()
  const intl = useIntl()
  const { language, tenant } = useGlobalConfigContext()
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true)
  const { addExperimentKeysForActivation } = useExperimentsContext()
  const [
    registrationApiErrorInvalidParamKeys,
    setRegistrationApiErrorInvalidParamKeys,
  ] = useState<string[]>([])
  const [formSubmissionError, setFormSubmissionError] = useState<boolean>(false)

  const { formData, hasVatNumber, isProfessionalRegistration, setFormData } =
    useContext(LoginRegistrationRegistrationStateContext)

  const formDataRef = useRef(formData)

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  const {
    experimentName,
    isModifyRegistrationExperimentEnabled,
    isStrongPasswordRequired,
    minPasswordLength,
  } = useModifyRegistrationExperiment()
  const registrationFlowConfig = resolveRegistrationFlowConfig({
    isModifyRegistrationExperimentEnabled,
    isStrongPasswordRequired,
  })
  const registrationOrigin = resolveRegistrationOrigin({
    hasTargetPageRedirectParam: searchParams.has(TARGET_PAGE_REDIRECT_PARAM),
    targetPageCode: searchParams.get(TARGET_PAGE_CODE_PARAM),
  })

  const registrationFormState = useMemo(() => {
    return registrationFormMakeRegistrationFormState({
      hasVatNumber,
      // eslint-disable-next-line react-hooks/refs -- don't reference, refactor if touched
      initialData: formDataRef.current,
      intl,
      isProfessionalRegistration,
      isStrongPasswordRequired,
      minPasswordLength,
    })
  }, [
    intl,
    isProfessionalRegistration,
    hasVatNumber,
    formDataRef,
    isStrongPasswordRequired,
    minPasswordLength,
  ])
  registrationFormState.use()
  const { model } = registrationFormState

  useEffect(() => {
    // back up form data, so we can use it as initial data
    // when updating the form validations based on hasVatNumber
    // and isProfessionalRegistration
    setFormData({
      birthDaySelectedDay: model.birthDaySelectedDay,
      birthDaySelectedMonth: model.birthDaySelectedMonth,
      birthDaySelectedYear: model.birthDaySelectedYear,
      companyName: model.companyName,
      customerType: model.customerType,
      email: model.email,
      fcSolution: model.fcSolution,
      firstName: model.firstName,
      lastName: model.lastName,
      newsletterAccepted: model.newsletterAccepted,
      password: model.password,
      phone: model.phone,
      salutation: model.salutation,
      tosAccepted: model.tosAccepted,
      vatNumber: model.vatNumber,
    })
  }, [
    model.birthDaySelectedDay,
    model.birthDaySelectedMonth,
    model.birthDaySelectedYear,
    model.companyName,
    model.email,
    model.fcSolution,
    model.firstName,
    model.lastName,
    model.newsletterAccepted,
    model.password,
    model.phone,
    model.salutation,
    model.tosAccepted,
    model.vatNumber,
    model.customerType,
    setFormData,
  ])

  useEffect(() => {
    handleValidationErrors(intl, registrationApiErrorInvalidParamKeys, model)
  }, [intl, registrationApiErrorInvalidParamKeys, model.validator, model])

  const registrationSwr = useUserRegistrationMutation({
    onError: (err: AxiosError<Authentication400>) => {
      setFormSubmissionError(true)
      setRegistrationApiErrorInvalidParamKeys(
        getRegistrationApiInvalidParamsErrorKeys(err),
      )

      registrationFormState.setValue('', model, 'fcSolution')
      registrationFormState.setValue(false, model, 'tosAccepted')
      registrationFormState.setValue('', model, 'password')
    },
    onSuccess: afterLoggedIn,
  })

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      registrationFormState.setValue(fcSolution, model, 'fcSolution')
      setSubmitButtonDisabled(false)
    },
    [registrationFormState, model],
  )

  useEffect(() => {
    if (isModifyRegistrationExperimentEnabled) {
      addExperimentKeysForActivation(experimentName)
    }
  }, [
    addExperimentKeysForActivation,
    isModifyRegistrationExperimentEnabled,
    experimentName,
  ])

  return (
    <LoginRegistrationRegistrationFormStateProvider
      registrationFormState={registrationFormState}
    >
      <form
        data-qa-id="register-form"
        id="register-form"
        onSubmit={(event): void => {
          event.preventDefault()
          void handleSubmit({
            hasVatNumber,
            isProfessionalRegistration,
            preferredLanguage: language,
            registrationFlowVersion:
              registrationFlowConfig.registrationFlowVersion,
            registrationFormState,
            registrationOrigin,
            tenant,
            userRegistrationSwr: registrationSwr,
          })
        }}
      >
        {formSubmissionError ? (
          <Notification className="mb-2 self-stretch" severity="error">
            <FormattedMessage id="general.error" />
          </Notification>
        ) : null}
        <div className="flex flex-col gap-3.5">
          <LoginRegistrationSalutation />
          <div className="grid grid-cols-2 gap-3">
            <LoginRegistrationRegistrationFormFirstNameInput />
            <LoginRegistrationRegistrationFormLastNameInput />
          </div>
          {isProfessionalRegistration ? (
            <>
              <LoginRegistrationRegistrationFormCompanyInput />
              <LoginRegistrationVatSelectionAndInput />
            </>
          ) : null}
          <LoginRegistrationRegistrationFormEmailInput />
          {registrationFlowConfig.isModifyRegistrationExperimentEnabled ? (
            <LoginRegistrationModifyRegistrationPasswordSection />
          ) : (
            <LoginRegistrationRegistrationFormPasswordInput
              isPasswordRequired
              isStrongPasswordRequired={false}
            />
          )}

          {TENANT.COM === tenant && (
            <LoginRegistrationRegistrationFormPhoneWithInfo />
          )}
          <LoginRegistrationBirthDaySelect />
          <LoginRegistrationNewsletterAcceptedCheckbox />
          <LoginRegistrationRegistrationFormTosAcceptedCheckbox />
          <FriendlyCaptchaChallenge
            className="mb-1.5"
            doneCallback={captchaDoneCallback}
            startMode="focus"
            whenThisPropChangesResetCaptcha={registrationSwr.error}
          />
          <LoginRegistrationRegistrationFormButtons
            isMutating={registrationSwr.isMutating}
            onClose={onClose}
            submitButtonDisabled={submitButtonDisabled}
          />
          <div className="text-dark-primary-strong">
            <FormattedMessage id="general.form.label.mandatoryFieldIndicator" />
          </div>
        </div>
      </form>
    </LoginRegistrationRegistrationFormStateProvider>
  )
}
